import * as React from 'react';
import addHours from 'date-fns/addHours';
import isAfter from 'date-fns/isAfter';
import moment from 'moment';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import {
    Table,
    FormGroup,
    ControlLabel,
    FormControl,
    Checkbox,
    Form,
    Col,
    Row
} from 'react-bootstrap';
import Select from 'react-select';
import Loader from 'react-loader';
import { Option } from 'react-select/lib/filters';
import { Translate } from '../../../shared/LanguageService';
import * as actions from '../OrdersActions';
import { handleError } from '../../../shared/ErrorCatching';
import * as Site from '../../../shared/Site';
import AutoUpdateListTrigger from '../../../shared/AutoUpdateListTrigger';
import DateTimeView from '../../localDate/DateTimeView';
import { LocalDateTime } from '../../localDate/DateTimeView';
import OrderStatusesIcons from '../../statusicons/StatusIcons';
import AssignUser from '../AssignUser';
import OrderListTableCell from '../../../components/common/OrderListTableCell';
import * as Features from '../../../shared/Features';
import * as Status from '../view/planOrders/PlanOrderStatus';
import CountryService from '../../../shared/CountryService';
import { IEnumeration } from '../Entities';
import { Add, Star } from '@mui/icons-material';

interface IProps {
    actions: IActions;
    planOrders: IPlanOrder[];
    user: any;
    router: any;
}

interface IState {
    filter: string;
    isLoading: boolean;
    showScheduledOrders: boolean;
    showOtherCountries: boolean;
    selectedCountries: Option[];
    selectableCountries: any[];
}

interface IPlanOrder {
    id: number;
    orderCollectionId: number;
    orderCollectionNumber: string;
    orderNumber: string;
    brokerFirmName: string;
    product: string;
    addressLine: string;
    startTime: LocalDateTime;
    customerReturnedDate?: LocalDateTime;
    roomSketcherStatus: IStatus;
    sketchUpStatus: IStatus;
    planUser: string;
    assignedUserName: string;
    isExpress: boolean;
    site: number;
    country: number;
    orderStatuses: any[];
    cubiCasaOrderStatus: IEnumeration;
    isReturned: boolean;
}

interface IStatus {
    id: number;
    name: string;
}

export function isPlanOrderLate(startDate: Date | null, customerReturnedDate: Date | null) {
    if (startDate === null) {
        return false;
    }
    let today = new Date(Date.now());
    if (customerReturnedDate !== null) {
        let lateReturnDate = addHours(customerReturnedDate, 24);
        let adjustedHoursForHoliday = getTimeDelayForHoliday(
            customerReturnedDate.getUTCDay(),
            customerReturnedDate
        );
        if (adjustedHoursForHoliday > 0) {
            lateReturnDate = addHours(lateReturnDate, adjustedHoursForHoliday);
            return isAfter(today, lateReturnDate);
        }
        return isAfter(today, lateReturnDate);
    }

    let lateDate = addHours(startDate, 25);
    let adjustedHoursForHoliday = getTimeDelayForHoliday(
        startDate.getUTCDay(),
        customerReturnedDate
    );
    if (adjustedHoursForHoliday > 0) {
        lateDate = addHours(lateDate, adjustedHoursForHoliday);
    }

    return isAfter(today, lateDate);
}

function isOrderLackingStartTime(startTime) {
    return startTime === '0001-01-01T01:00:00';
}

export enum Weekday {
    Monday = 1,
    Tuesday = 2,
    Wednesday = 3,
    Thursday = 4,
    Friday = 5,
    Saturday = 6,
    Sunday = 0
}
export function getTimeDelayForHoliday(weekday: Weekday, customerReturnedDate: Date | null) {
    if (weekday === Weekday.Saturday) {
        return 24;
    } else if (
        weekday === Weekday.Friday ||
        (weekday === Weekday.Thursday && customerReturnedDate)
    ) {
        return 48;
    }
    return 0;
}

class PlanOrderList extends React.Component<IProps, IState> {
    private _updateListTrigger: AutoUpdateListTrigger;

    constructor(props: IProps) {
        super(props);

        this.state = {
            filter: '',
            isLoading: false,
            showScheduledOrders: false,
            showOtherCountries: false,
            selectedCountries: [],
            selectableCountries: []
        };

        this.getPlanOrders = this.getPlanOrders.bind(this);
        this.filterOnChange = this.filterOnChange.bind(this);
        this.filterByOrderCollectionNumberOrAddress =
            this.filterByOrderCollectionNumberOrAddress.bind(this);
        this.removeScheduledForDeliveryOrders = this.removeScheduledForDeliveryOrders.bind(this);
        this.onShowScheduledOrdersChanged = this.onShowScheduledOrdersChanged.bind(this);
        this.handleSelectedCountriesChange = this.handleSelectedCountriesChange.bind(this);
        this.filterBySelectedCountries = this.filterBySelectedCountries.bind(this);
        this._updateListTrigger = new AutoUpdateListTrigger(this.getPlanOrders);
    }

    componentDidMount(): void {
        let selectedCountries = JSON.parse(
            window.localStorage.getItem('orderListSelectedCountries')
        );

        if (
            !selectedCountries ||
            !Array.isArray(selectedCountries) ||
            selectedCountries.length === 0
        ) {
            let defaultCountryId = CountryService.getDefaultOrImpersonatedCountryId();
            let translationKey = CountryService.getCountryTranslationKey(defaultCountryId);
            selectedCountries = [{ value: defaultCountryId, label: Translate(translationKey) }];

            window.localStorage.setItem(
                'orderListSelectedCountries',
                JSON.stringify(selectedCountries)
            );
        }

        let selectableCountries = [
            {
                value: CountryService.mapCountryCodeToId('sv'),
                label: Translate('country/sweden')
            },
            {
                value: CountryService.mapCountryCodeToId('fi'),
                label: Translate('country/finland')
            },
            {
                value: CountryService.mapCountryCodeToId('es'),
                label: Translate('country/spain')
            },
            {
                value: CountryService.mapCountryCodeToId('fr'),
                label: Translate('country/france')
            }
        ];

        this.setState({ selectedCountries, selectableCountries } as IState, () =>
            this.getPlanOrders()
        );

        this._updateListTrigger.subscribe();
    }

    componentWillUnmount() {
        this._updateListTrigger.unsubscribe();
    }

    getPlanOrders() {
        if (!this.state.isLoading) {
            this.setState({ isLoading: true } as IState);
            this.props.actions
                .getPlanOrders()
                .then(() => {
                    this.setState({ isLoading: false } as IState);
                })
                .catch((error: any) => {
                    this.setState({ isLoading: false } as IState);
                    return handleError(error);
                });
        }
    }

    filterOnChange(e: any) {
        this.setState({ filter: e.target.value } as IState);
    }

    filterByOrderCollectionNumberOrAddress(planOrder: IPlanOrder) {
        if (!this.state.filter || this.state.filter === '') {
            return true;
        }

        let filter = this.state.filter.toLowerCase();
        return (
            planOrder.orderCollectionNumber.toLowerCase().includes(filter) ||
            planOrder.addressLine.toLowerCase().includes(filter)
        );
    }

    removeScheduledForDeliveryOrders(planorder: IPlanOrder) {
        if (this.state.showScheduledOrders) {
            return true;
        }
        if (
            planorder.sketchUpStatus.id === Status.scheduledForDelivery ||
            planorder.roomSketcherStatus.id === Status.scheduledForDelivery
        ) {
            return false;
        }
        return true;
    }

    filterBySelectedCountries(planOrder: IPlanOrder): boolean {
        if (this.state.selectedCountries) {
            let filterCountries = this.state.selectedCountries.filter((country: any) => {
                return country.value !== 0;
            });

            return filterCountries.some((x: any) => {
                return x.value === planOrder.country;
            });
        }
        return true;
    }

    getOrderRowCssClass(order: IPlanOrder) {
        let startDate = isOrderLackingStartTime(order.startTime.localDateTime)
            ? null
            : new Date(order.startTime.localDateTime);
        let customerReturnedDate =
            order.customerReturnedDate === null
                ? null
                : new Date(order.customerReturnedDate.localDateTime);

        let isLate = isPlanOrderLate(startDate, customerReturnedDate);

        return (
            Site.getOrderCssClass(order.site) +
            (order.isExpress === true ? ' express' : '') +
            (isLate ? ' late' : '')
        );
    }

    renderStatus(status: IStatus) {
        if (status.id === 1 /*Created*/) {
            return;
        }

        if (status.id === 11 /*Not ordered*/) {
            return;
        }

        return Translate(status.name);
    }

    onShowScheduledOrdersChanged(e: any) {
        let showScheduledOrders = this.state.showScheduledOrders;
        showScheduledOrders = e.target.checked;
        this.setState({ showScheduledOrders: showScheduledOrders } as IState);
    }

    handleSelectedCountriesChange(value: any) {
        window.localStorage.setItem('orderListSelectedCountries', JSON.stringify(value));
        this.setState({ selectedCountries: value } as IState);
    }

    renderStatusClass(status: IStatus) {
        if (status.id === 1 /* Created*/) {
            return;
        }

        let cssClass = '';

        switch (status.id) {
            case 1:
                cssClass = 'status status1';
                break;
            case 2:
                cssClass = 'status status2';
                break;
            case 3:
                cssClass = 'status status3';
                break;
            case 4:
                cssClass = 'status status4';
                break;
            case 5:
                cssClass = 'status status5';
                break;
            case 6:
                cssClass = 'status status6';
                break;
            case 7:
                cssClass = 'status status7';
                break;
            case 8:
                cssClass = 'status status8';
                break;
            case 9:
                cssClass = 'status status9';
                break;
            case 10:
                cssClass = 'status status10';
                break;
            default:
                break;
        }

        return cssClass;
    }

    renderCubiCasaStatusClass(status: IEnumeration) {
        let cssClass = '';

        switch (status.value) {
            case 1:
                cssClass = 'status cubicasa-status cubicasa-status--1';
                break;
            case 2:
                cssClass = 'status cubicasa-status cubicasa-status--2';
                break;
            case 3:
                cssClass = 'status cubicasa-status cubicasa-status--3';
                break;
            default:
                break;
        }

        return cssClass;
    }

    render() {
        const canViewOrder = Features.hasFeature(Features.ViewOrder, this.props.user);
        const orders: IPlanOrder[] = this.props.planOrders
            .filter(this.filterByOrderCollectionNumberOrAddress)
            .filter(this.removeScheduledForDeliveryOrders)
            .filter(this.filterBySelectedCountries);

        return (
            <div>
                <h1>
                    {Translate('orders/title/plans')}{' '}
                    {!this.state.isLoading && (
                        <span className="number-of-orders">({orders.length})</span>
                    )}
                </h1>
                <Form>
                    <FormGroup>
                        <Col sm={8}>
                            <Row>
                                <ControlLabel>{Translate('orders/list/filter')}</ControlLabel>
                            </Row>
                            <Row>
                                <FormControl
                                    type="text"
                                    name="filter"
                                    data-testid="filter-text"
                                    value={this.state.filter ? this.state.filter : ''}
                                    onChange={this.filterOnChange}
                                />
                            </Row>
                        </Col>
                        <Col sm={4}>
                            <Row>
                                <ControlLabel>
                                    {Translate('plan-order/select/country-checkbox')}
                                </ControlLabel>
                            </Row>
                            <Row>
                                <Select
                                    isMulti
                                    closeMenuOnSelect={false}
                                    placeholder={Translate('plan-order/select/country-placeholder')}
                                    value={this.state.selectedCountries}
                                    onChange={this.handleSelectedCountriesChange}
                                    options={this.state.selectableCountries}
                                />
                            </Row>
                        </Col>
                    </FormGroup>
                    <FormGroup>
                        <Col sm={2} style={{ padding: 0 }}>
                            <Checkbox
                                name="showScheduledOrders"
                                checked={this.state.showScheduledOrders}
                                onChange={this.onShowScheduledOrdersChanged}
                            >
                                <span>{Translate('orders/list/filter/show-scheduled-orders')}</span>
                            </Checkbox>
                        </Col>
                    </FormGroup>
                </Form>
                <Loader loaded={!this.state.isLoading}>
                    <Table className="order-table table-nopadding" responsive hover data-testid="order-table">
                        <thead>
                            <tr>
                                <th>{Translate('orders/list/plan/header/order-number')}</th>
                                <th>{Translate('orders/list/plan/header/broker-firm')}</th>
                                <th>{Translate('orders/list/plan/header/product')}</th>
                                <th>{Translate('orders/list/plan/header/address')}</th>
                                <th>{Translate('orders/list/plan/header/start-time')}</th>
                                <th>{Translate('orders/list/plan/header/cubi-casa')}</th>
                                <th>{Translate('orders/list/plan/header/sketchup-status')}</th>
                                <th>{Translate('orders/list/plan/header/roomsketcher-status')}</th>
                                <th>{Translate('orders/list/plan/header/plan-user')}</th>
                                <th>{Translate('orders/list/plan/header/assigned-user')}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((x) => (
                                <tr
                                    key={'planlist-' + x.id}
                                    className={this.getOrderRowCssClass(x)}
                                    data-testid="order-row"
                                >
                                    <td>
                                        <Link to={`/ordercollections/${x.orderCollectionId}`}>
                                            {x.orderCollectionNumber}
                                        </Link>
                                        :{x.orderNumber}
                                    </td>
                                    <OrderListTableCell
                                        clickable={canViewOrder}
                                        navigateTo={`/orders/${x.id}/plan`}
                                    >
                                        {x.brokerFirmName}
                                    </OrderListTableCell>
                                    <OrderListTableCell
                                        clickable={canViewOrder}
                                        navigateTo={`/orders/${x.id}/plan`}
                                    >
                                        {x.product}
                                    </OrderListTableCell>
                                    <OrderListTableCell
                                        clickable={canViewOrder}
                                        navigateTo={`/orders/${x.id}/plan`}
                                    >
                                        {x.addressLine}
                                    </OrderListTableCell>
                                    <OrderListTableCell
                                        clickable={canViewOrder}
                                        navigateTo={`/orders/${x.id}/plan`}
                                        className="nobreak"
                                    >
                                        {this.getStartTimeField(
                                            x.startTime,
                                            x.customerReturnedDate
                                        )}
                                    </OrderListTableCell>
                                    {x.cubiCasaOrderStatus ? (
                                        <OrderListTableCell
                                            clickable={canViewOrder}
                                            navigateTo={`/orders/${x.id}/plan`}
                                            className={this.renderCubiCasaStatusClass(
                                                x.cubiCasaOrderStatus
                                            )}
                                            title={Translate(x.cubiCasaOrderStatus.displayName)}
                                        ></OrderListTableCell>
                                    ) : (
                                        <OrderListTableCell
                                            clickable={canViewOrder}
                                            navigateTo={`/orders/${x.id}/plan`}
                                        ></OrderListTableCell>
                                    )}
                                    <OrderListTableCell
                                        clickable={canViewOrder}
                                        navigateTo={`/orders/${x.id}/plan`}
                                        className={this.renderStatusClass(x.sketchUpStatus)}
                                    >
                                        {this.renderStatus(x.sketchUpStatus)}
                                    </OrderListTableCell>
                                    <OrderListTableCell
                                        clickable={canViewOrder}
                                        navigateTo={`/orders/${x.id}/plan`}
                                        className={this.renderStatusClass(x.roomSketcherStatus)}
                                    >
                                        {this.renderStatus(x.roomSketcherStatus)}
                                    </OrderListTableCell>
                                    <OrderListTableCell
                                        clickable={canViewOrder}
                                        navigateTo={`/orders/${x.id}/plan`}
                                    >
                                        {x.planUser}
                                    </OrderListTableCell>
                                    {x.assignedUserName && (
                                        <OrderListTableCell
                                            clickable={canViewOrder}
                                            navigateTo={`/orders/${x.id}/plan`}
                                        >
                                            {x.assignedUserName}
                                        </OrderListTableCell>
                                    )}
                                    {!x.assignedUserName && (
                                        <td>
                                            <AssignUser user={this.props.user} orderId={x.id} />
                                        </td>
                                    )}
                                    <OrderListTableCell
                                        clickable={canViewOrder}
                                        navigateTo={`/orders/${x.id}/plan`}
                                    >
                                        <OrderStatusesIcons
                                            orderStatuses={x.orderStatuses}
                                            isPlanOrder={true}
                                        ></OrderStatusesIcons>
                                    </OrderListTableCell>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Loader>
            </div>
        );
    }

    getStartTimeField(startTime: LocalDateTime, customerReturnedDate?: LocalDateTime) {
        let mainDate = startTime;
        let originalDate: LocalDateTime;

        if (startTime.localDateTime.toString() === '0001-01-01T01:00:00') {
            return <div />;
        }

        if (customerReturnedDate) {
            mainDate = customerReturnedDate;
            originalDate = startTime;
        }

        return (
            <div>
                <DateTimeView dateTime={mainDate} format={'L LT'} />
                {originalDate && (
                    <div
                        title={`${Translate(
                            'orders/list/plan/start-time/original/title'
                        )}: ${moment(originalDate.originalDateTime).format('L LT')}`}
                    >
                        &nbsp;
                        {`(${moment(originalDate.localDateTime).date()}/${
                            moment(originalDate.localDateTime).month() + 1
                        })`}
                    </div>
                )}
            </div>
        );
    }
}

function mapStateToProps(state: any, ownProps: IProps) {
    return {
        planOrders: state.orders.list,
        user: state.user
    };
}

function mapDispatchToProps(dispatch: any): { actions: IActions } {
    return {
        actions: {
            getPlanOrders: () => dispatch(actions.getPlanOrders())
        }
    };
}

interface IActions {
    getPlanOrders: () => Promise<any>;
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(PlanOrderList));
